import React, { useEffect } from 'react';
import {
  Box,
  Chip,
  Typography,
  Tooltip,
  Badge,
  Fade,
} from '@mui/material';
import {
  Wifi as ConnectedIcon,
  WifiOff as DisconnectedIcon,
  People as PeopleIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { 
  updateActiveUsers, 
  addNotification,
  simulateVoteUpdate 
} from '../../services/slices/realTimeSlice';

const RealTimeStatus: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isConnected, activeUsers } = useAppSelector((state) => state.realTime);

  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      // Randomly update active users count
      const change = Math.floor(Math.random() * 6) - 3; // -3 to +3
      const newCount = Math.max(20, Math.min(100, activeUsers + change));
      dispatch(updateActiveUsers(newCount));

      // Occasionally add notifications
      if (Math.random() < 0.3) {
        const notifications = [
          { message: 'New user joined the session', type: 'user_joined' as const },
          { message: 'Vote submitted for Question 1', type: 'vote' as const },
          { message: 'User left the session', type: 'user_left' as const },
        ];
        const randomNotification = notifications[Math.floor(Math.random() * notifications.length)];
        dispatch(addNotification(randomNotification));
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [dispatch, activeUsers]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {/* Connection Status */}
      <Tooltip title={isConnected ? 'Connected to real-time updates' : 'Disconnected'}>
        <Chip
          icon={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isConnected ? (
                <ConnectedIcon sx={{ fontSize: 16 }} />
              ) : (
                <DisconnectedIcon sx={{ fontSize: 16 }} />
              )}
              {isConnected && (
                <CircleIcon 
                  sx={{ 
                    fontSize: 8, 
                    color: 'success.main',
                    ml: 0.5,
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': {
                        opacity: 1,
                      },
                      '50%': {
                        opacity: 0.5,
                      },
                      '100%': {
                        opacity: 1,
                      },
                    },
                  }} 
                />
              )}
            </Box>
          }
          label={isConnected ? 'Live' : 'Offline'}
          color={isConnected ? 'success' : 'error'}
          variant="outlined"
          size="small"
        />
      </Tooltip>

      {/* Active Users Count */}
      <Tooltip title="Active participants">
        <Fade in timeout={500}>
          <Chip
            icon={<PeopleIcon sx={{ fontSize: 16 }} />}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" fontWeight="bold">
                  {activeUsers}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  online
                </Typography>
              </Box>
            }
            color="primary"
            variant="outlined"
            size="small"
            sx={{
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          />
        </Fade>
      </Tooltip>
    </Box>
  );
};

export default RealTimeStatus;
